




import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { WeatherData } from "@/api/weather/types";
import ResizeObserver from 'resize-observer-polyfill';
import InfoTooltip from '@/components/infoTooltip/InfoTooltip.vue';


@Component({
  name: "SPI4ChartHighChart",
})
export default class SPI4Chart extends Vue {
  @Prop({ type: Array, required: true })
  private averageData!: WeatherData[];

  @Prop({ type: Array, required: true })
  private historicalData!: WeatherData[];

  private chartOptions: any = {}; 

  private labels: string[] = [];
  private ro: ResizeObserver | null = null;

  @Watch("historicalData", { immediate: true })
  private historicalDataChanged(historicalData: WeatherData[]) {
    requestAnimationFrame(() => {
      this.$set(this.chartOptions, 'series', this.loadedData(this.averageData, this.historicalData));
      this.setLabels();
    });
  }

  private loadedData(averageDataParam: WeatherData[], historicalDataParam: WeatherData[]): any {
    const averageData = averageDataParam.sort((x, y) => (x.weatherMonth + (x.weatherYear * 100)) - (y.weatherMonth + (y.weatherYear * 100)));
    const historicalData: WeatherData[] = [];

    averageData.forEach(x => this.historicalData.forEach(y => y.weatherMonth == x.weatherMonth ? historicalData.push(y) : ''));

    this.labels = averageData.map(weather => `${this.getMonth(weather.weatherMonth)}/${weather.weatherYear%100}`);
    return [
      {
        name: this.$t('charts.spi4.current') as string,
        data: averageData.map(weather => weather.weatherMedian),
        zIndex: 1,
        color: "#00fca1",
        marker: {
          radius: 3,
          lineColor: "#00fca1",
          fillColor: "#00fca1",
        },
        tooltip: {
          valueDecimals: 2,
        },
        type: 'spline',
      },
      {
        name: this.$t('charts.spi4.historical') as string,
        data: historicalData.map(weather => weather.weatherMedian),
        color: "white",
        dashStyle: 'ShortDash',
        marker: {
          radius: 3,
          lineColor: "white",
          fillColor: "white",
        },
        tooltip: {
          valueDecimals: 2,
        },
        type: 'spline',
      },
      {
        type: 'areasplinerange',
        data: this.buildRange(historicalData),
        fillOpacity: 0.3,
        linkedTo: ':previous',
        color: "white",
        lineWidth: 0,
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
      }
    ];
  }

  private buildStd(waeatherDataList: WeatherData[]): Object {
    let st :any = {};
    waeatherDataList.forEach(it => st[this.getMonth(it.weatherMonth)] = { plus: it.weatherStd, minus: it.weatherStd });
    return st;
  }

      
  private buildRange(weatherDataList: WeatherData[]): Object {
    return weatherDataList.map(it => [
      it.weatherMedian - it.weatherStd,
      it.weatherMedian + it.weatherStd
    ]);
  }

  private getMonth(month: number): string {
      return this.$t(`charts.months.${month}`) as string;
  }

  private setLabels() {
    this.$set(this.chartOptions.xAxis[0], 'categories', this.labels);
  }

  private redrawChartOnWillAppear() {
    let lastHeight = 0;
    let lastWidth = 0;

    this.ro = new ResizeObserver((entries, observer) => {
      for (const entry of entries) {
        const { height, width} = entry.contentRect;
        if ((height !== lastHeight  || width !== lastWidth)) {
          this.$nextTick(() => {
            this.reflow();
          });
        }
        lastHeight = height;
        lastWidth = width
      }
    });
    this.ro.observe((this.$refs.hcComponent! as Vue).$el);
  }

  public reflow() {
    // @ts-ignore
    this.$refs.hcComponent?.chart?.reflow();
  }


  private async mounted() {
    this.chartOptions = {
      chart: {
        style: {
          fontFamily: 'BlinkMacSystemFont,-apple-system,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,Arial,sans-serif'
        },
        backgroundColor: '#12191c',
      },
      credits: {
        enabled: false,
      },
      navigation: {
        menuStyle: {
          'font-family': 'BlinkMacSystemFont,-apple-system,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,Helvetica,Arial,sans-serif',
        }
      },
      title: {
        text: `${this.$t('charts.spi4.name')} <span id="info-window-spi"></span>`,
        style: {
          fontSize: '12px',
          fontWeight: 'bold'
        },
        useHTML: true,
      },
      xAxis: [{
        categories: [],
      }],
      yAxis: [{
        title: {
          enabled: false,
        },
        softMax: 2.5,
        softMin: -2.5,
        tickInterval: 0.5,
        minPadding: 0,
        maxPadding: 0,
      }],
      tooltip: {
        crosshairs: true,
        shared: true
      },
      series: this.loadedData(this.averageData, this.historicalData),
    }

    this.setLabels();
    this.redrawChartOnWillAppear();
    await this.$nextTick();
    new InfoTooltip({propsData: {title: this.$t('info-tooltip.spi'), tooltipPlacement: 'bottom'}})
    .$mount(document.querySelector('#info-window-spi')!);
  }
}
